import React, { useState, useEffect, useCallback, useRef } from 'react';
import DatePicker from 'react-datepicker';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import Swal from 'sweetalert2';
import { suppliesService } from '../../services/supplies';
import { productsService } from '../../services/products';
import { usersService } from '../../services/users';
import { useAuth } from '../../hooks/useAuth';
import type { 
  SupplyOrder, 
  SupplyOrderFilters, 
  PaginationInfo, 
  Branch,
  FollowUp 
} from '../../types/supplies';
import type { User } from '../../types/auth';
import 'react-datepicker/dist/react-datepicker.css';

// Sticky header CSS will be set dynamically

const SupplyOrders: React.FC = () => {
  // Modal for supply order number input - removed, using Swal instead
  // Ref for filters container
  const filtersRef = useRef<HTMLDivElement>(null);
  // Removed JS sticky logic; using only CSS sticky for header/filter
  // View filter state
  const [viewDropdownOpen, setViewDropdownOpen] = useState(false);
  type ViewFilterKey =
    | 'allViewed'
    | 'photosViewed'
    | 'notesViewed'
    | 'followUpViewed'
    | 'allUnViewed'
    | 'photosUnViewed'
    | 'notesUnViewed'
    | 'followUpUnViewed';

  type ViewFilter = {
    [K in ViewFilterKey]: boolean;
  };

  // Use a pending filter for UI, only apply on refresh
  const [viewFilter, setViewFilter] = useState<ViewFilter>({
    allViewed: false,
    photosViewed: false,
    notesViewed: false,
    followUpViewed: false,
    allUnViewed: false,
    photosUnViewed: false,
    notesUnViewed: false,
    followUpUnViewed: false
  });
  const [pendingViewFilter, setPendingViewFilter] = useState<ViewFilter>({
    allViewed: false,
    photosViewed: false,
    notesViewed: false,
    followUpViewed: false,
    allUnViewed: false,
    photosUnViewed: false,
    notesUnViewed: false,
    followUpUnViewed: false
  });
  // Track if view filter is active (when true we rely on server-side filtering)
  const [viewFilterActive, setViewFilterActive] = useState(false);

  // Label for dropdown button

  // Handle logic for radio/checkbox exclusivity
  const handleViewFilterChange = (key: ViewFilterKey, value: boolean) => {
    // Prevent selecting both viewed and unviewed for the same category
    const oppositeMap: Record<ViewFilterKey, ViewFilterKey> = {
      photosViewed: 'photosUnViewed',
      photosUnViewed: 'photosViewed',
      notesViewed: 'notesUnViewed',
      notesUnViewed: 'notesViewed',
      followUpViewed: 'followUpUnViewed',
      followUpUnViewed: 'followUpViewed',
      allViewed: 'allUnViewed',
      allUnViewed: 'allViewed'
    };
    if (key === 'allViewed') {
      setPendingViewFilter({
        allViewed: value,
        photosViewed: false,
        notesViewed: false,
        followUpViewed: false,
        allUnViewed: false,
        photosUnViewed: false,
        notesUnViewed: false,
        followUpUnViewed: false
      });
    } else if (key === 'allUnViewed') {
      setPendingViewFilter({
        allViewed: false,
        photosViewed: false,
        notesViewed: false,
        followUpViewed: false,
        allUnViewed: value,
        photosUnViewed: false,
        notesUnViewed: false,
        followUpUnViewed: false
      });
    } else if (oppositeMap[key]) {
      setPendingViewFilter((prev) => ({
        ...prev,
        [key]: value,
        [oppositeMap[key]]: value ? false : prev[oppositeMap[key]],
        allViewed: false,
        allUnViewed: false
      }));
    } else {
      setPendingViewFilter((prev) => ({
        ...prev,
        [key]: value,
        allViewed: false,
        allUnViewed: false
      }));
    }
    // Do NOT update viewFilter or backend filters here. Only update pendingViewFilter.
  };
  // Extend SupplyOrder locally to include 'viewed' property for UI state
  type SupplyOrderWithViewed = SupplyOrder & {
    notesViewed?: boolean;
    photosViewed?: boolean;
    followUpsViewed?: boolean;
  };
  const [orders, setOrders] = useState<SupplyOrderWithViewed[]>([]);
    const getFilteredOrders = () => {
        // If we applied a server-side view filter (via Refresh), show the server-returned rows
        if (viewFilterActive) {
          let filtered = orders;
          if (filters.branch_stock_status) {
            filtered = filtered.filter(order => order.branch_stock_status === filters.branch_stock_status);
          }
          if (filters.store_stock_status) {
            filtered = filtered.filter(order => order.store_stock_status === filters.store_stock_status);
          }
          // Apply Has Follow Up filter when active
          if (filters.hasFollowUp === 'yes') {
            filtered = filtered.filter(order => order.follow_ups && order.follow_ups.length > 0);
          } else if (filters.hasFollowUp === 'no') {
            filtered = filtered.filter(order => !order.follow_ups || order.follow_ups.length === 0);
          }
          return applySorting(filtered);
        }

        if (viewFilter.allViewed) {
        // Treat null as '111' for filtering
          return applySorting(orders.filter(order => {
            const isViewed = order.is_viewed == null ? '111' : order.is_viewed;
            return isViewed === '111';
          }));
      }
      if (viewFilter.photosViewed) {
          return applySorting(orders.filter(order => {
            const isViewed = order.is_viewed;
            // Only show items where 2nd digit is '1' and photos are present
            return isViewed && isViewed.length === 3 && isViewed[1] === '1' && order.photos && order.photos.length > 0;
          }));
      }
      if (viewFilter.notesViewed) {
          return applySorting(orders.filter(order => {
            const isViewed = order.is_viewed;
            // Only show items where 1st digit is '1' and notes are present
            return isViewed && isViewed.length === 3 && isViewed[0] === '1' && order.notes && order.notes.trim() !== '';
          }));
      }
      if (viewFilter.followUpViewed) {
          return applySorting(orders.filter(order => {
            const isViewed = order.is_viewed;
            // Only show items where 3rd digit is '1' and follow_ups are present
            return isViewed && isViewed.length === 3 && isViewed[2] === '1' && order.follow_ups && order.follow_ups.length > 0;
          }));
      }
      if (viewFilter.allUnViewed) {
          return applySorting(orders.filter(order => {
            const isViewed = order.is_viewed == null ? '111' : order.is_viewed;
            return isViewed === '000';
          }));
      }
      if (viewFilter.photosUnViewed) {
          return applySorting(orders.filter(order => {
            const isViewed = order.is_viewed == null ? '111' : order.is_viewed;
            return (
              isViewed === '000' ||
              isViewed === '001' ||
              isViewed === '100'
            );
          }));
      }
      if (viewFilter.notesUnViewed) {
          return applySorting(orders.filter(order => {
            const isViewed = order.is_viewed == null ? '111' : order.is_viewed;
            return (
              isViewed === '000' ||
              isViewed === '001' ||
              isViewed === '010'
            );
          }));
      }
      if (viewFilter.followUpUnViewed) {
          return applySorting(orders.filter(order => {
            const isViewed = order.is_viewed == null ? '111' : order.is_viewed;
            return (
              isViewed === '000' ||
              isViewed === '010' ||
              isViewed === '100'
            );
          }));
      }
      let filtered = orders;
      if (filters.branch_stock_status) {
        filtered = filtered.filter(order => order.branch_stock_status === filters.branch_stock_status);
      }
      if (filters.store_stock_status) {
        filtered = filtered.filter(order => order.store_stock_status === filters.store_stock_status);
      }
      // Apply Has Follow Up filter when active
      if (filters.hasFollowUp === 'yes') {
        filtered = filtered.filter(order => order.follow_ups && order.follow_ups.length > 0);
      } else if (filters.hasFollowUp === 'no') {
        filtered = filtered.filter(order => !order.follow_ups || order.follow_ups.length === 0);
      }
        return applySorting(filtered);
  };
  type NoteObj = { text: string; timestamp: string; username?: string };
  const [notesModal, setNotesModal] = useState<{ show: boolean; orderId: number | null; notes: NoteObj[]; newNote: string; isAdding: boolean }>({ show: false, orderId: null, notes: [], newNote: '', isAdding: false });
  const { user } = useAuth();
  const [branches, setBranches] = useState<Branch[]>([]);
  const [productClasses, setProductClasses] = useState<string[]>([]);
  const [purchaseUsers, setPurchaseUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [pagination, setPagination] = useState<PaginationInfo>({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    recordsPerPage: 5
  });
  // Sorting state: field -> column key, order -> 'desc' | 'asc' | undefined
  const [sortState, setSortState] = useState<{ field?: string; order?: 'asc' | 'desc' | undefined }>({});

  // Actual filters used for backend
  const [filters, setFilters] = useState<SupplyOrderFilters>({
    page: 1,
    limit: 5,
    branch_stock_status: undefined,
    store_stock_status: undefined
  });
  // Pending filters for UI (only applied on refresh)
  const [pendingFilters, setPendingFilters] = useState<SupplyOrderFilters>({
    page: 1,
    limit: 5,
    branch_stock_status: undefined,
    store_stock_status: undefined
  });

  const [editingAssignment, setEditingAssignment] = useState<{ [key: number]: boolean }>({});
  const [tempAssignments, setTempAssignments] = useState<{ [key: number]: number | null }>({});
  const [editingStatus, setEditingStatus] = useState<{ [key: number]: boolean }>({});
  const [tempStatus, setTempStatus] = useState<{ [key: number]: string }>({});
  const [editingSupplier, setEditingSupplier] = useState<{ [key: number]: boolean }>({});
  const [tempSupplier, setTempSupplier] = useState<{ [key: number]: string }>({});
  const [supplierTab, setSupplierTab] = useState<{ [key: number]: 'existing' | 'new' }>({});
  const [existingSuppliers, setExistingSuppliers] = useState<string[]>([]);
  const [supplierSearchTerm, setSupplierSearchTerm] = useState<{ [key: number]: string }>({});
  const [selectedOrders, setSelectedOrders] = useState<Set<number>>(new Set());

  const canAssignOrders = () => {
    if (!user) return false;
    return user.role === 'Purchase admin' || 
           user.role === 'Purchase supervisor' || 
           user.role === 'Purchase agent' ||
           user.role === 'super admin';
  };
  // Helper: cycle sort for a given field: undefined -> desc -> asc -> undefined
  const handleHeaderSort = (field: string) => {
    setSortState(prev => {
      if (prev.field !== field) return { field, order: 'desc' };
      if (prev.order === 'desc') return { field, order: 'asc' };
      return {}; // clear sorting
    });
  };

  // Apply sorting to filtered results
  const applySorting = (rows: SupplyOrderWithViewed[]) => {
    if (!sortState.field) return rows;
    const field = sortState.field;
    const orderMultiplier = sortState.order === 'asc' ? 1 : -1;
    const getVal = (row: SupplyOrderWithViewed) => {
      switch (field) {
        case 'id': return row.id ?? 0;
        case 'branch': return (row.branch && row.branch.branch) ? row.branch.branch.toString().toLowerCase() : '';
        case 'created_by': return (row.created_by || '').toString().toLowerCase();
        case 'call_center': return (row.call_center || '').toString();
        case 'order_date': return new Date(row.order_date || 0).getTime();
        case 'item_code': return (row.item_code || '').toString().toLowerCase();
        case 'item_name': return ((row.product && row.product.item_name) || row.item_name || '').toString().toLowerCase();
        case 'quantity': return Number(row.quantity) || 0;
        case 'item_price': return Number(row.item_price) || 0;
        case 'total_price': return Number(row.total_price) || 0;
        case 'supplier': return (row.supplier || '').toString().toLowerCase();
        case 'prod_class': return (row.prod_class || '').toString().toLowerCase();
        case 'last_followup_name': return (row.follow_ups && row.follow_ups.length) ? (row.follow_ups[row.follow_ups.length - 1].user_name || '').toString().toLowerCase() : '';
        case 'last_followup_date': return (row.follow_ups && row.follow_ups.length) ? new Date(row.follow_ups[row.follow_ups.length - 1].created_at).getTime() : 0;
        case 'notes_count': return row.notes ? row.notes.split('\n---\n').length : 0;
        case 'photos_count': return row.photos ? row.photos.length : 0;
        case 'followups_count': return row.follow_ups ? row.follow_ups.length : 0;
        case 'branch_stock_status': return (row.branch_stock_status || '').toString().toLowerCase();
        case 'store_stock_status': return (row.store_stock_status || '').toString().toLowerCase();
        case 'assigned_to_name': return (row.assigned_to_name || '').toString().toLowerCase();
        case 'status': return (row.status || '').toString().toLowerCase();
        case 'updated_by_name': return (row.updated_by_name || '').toString().toLowerCase();
        case 'updated_at': return new Date(row.updated_at || 0).getTime();
        case 'supply_order_number': return row.supply_order_number ?? '';
        default: return '';
      }
    };

    const sorted = [...rows].sort((a, b) => {
      const va = getVal(a);
      const vb = getVal(b);
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * orderMultiplier;
      if (va < vb) return -1 * orderMultiplier;
      if (va > vb) return 1 * orderMultiplier;
      return 0;
    });
    return sorted;
  };

  const renderSortIcon = (field: string) => {
    if (sortState.field !== field) return <i className="bi bi-arrow-repeat text-muted ms-1" />;
    if (sortState.order === 'desc') return <i className="bi bi-arrow-down-short ms-1" />;
    if (sortState.order === 'asc') return <i className="bi bi-arrow-up-short ms-1" />;
    return <i className="bi bi-arrow-repeat text-muted ms-1" />;
  };

  const canManageSuppliers = canAssignOrders;
  
  // Handle individual order selection
  const handleSelectOrder = (orderId: number) => {
    setSelectedOrders(prev => {
      const newSelected = new Set(prev);
      if (newSelected.has(orderId)) {
        newSelected.delete(orderId);
      } else {
        newSelected.add(orderId);
      }
      return newSelected;
    });
  };

  // Check if an order is selected
  const isOrderSelected = (orderId: number) => {
    return selectedOrders.has(orderId);
  };
  
  const [photoModal, setPhotoModal] = useState<{ 
    show: boolean; 
    photos: string[]; 
    currentIndex: number;
    orderId?: number;
  }>({
    show: false,
    photos: [],
    currentIndex: 0
  });
  
  const [followUpModal, setFollowUpModal] = useState<{
    show: boolean;
    orderId: number | null;
    followUps: FollowUp[];
    isAdding: boolean;
    newNotes: string;
    newPhotos: string[];
    newStatus: string;
  }>({
    show: false,
    orderId: null,
    followUps: [],
    isAdding: false,
    newNotes: '',
    newPhotos: [],
    newStatus: ''
  });

  // Per-follow-up expanded state removed — View opens photo modal directly

  const [photoUploadModal, setPhotoUploadModal] = useState<{
    show: boolean;
    orderId: number | null;
  }>({
      show: false,
      orderId: null
  });

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-warning';
      case 'done':
        return 'bg-success';
      case 'unavailable':
        return 'bg-danger';
      case 'limited stock':
        return 'bg-primary';
      default:
        return 'bg-secondary';
    }
  };

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      // Load users for assignment dropdown based on current user's role
      if (canAssignOrders() && user) {
        let usersResponse;
        if (user.role === 'super admin' || user.role === 'Purchase supervisor') {
          usersResponse = await usersService.getUsers({ is_active: true });
        } else if (user.role === 'Purchase admin' || user.role === 'Purchase agent') {
          usersResponse = await usersService.getUsers({ department: 'Purchase', is_active: true });
        } else if (user.role === 'Supplies admin') {
          usersResponse = await usersService.getUsers({ department: 'Supplies', is_active: true });
        }
        setPurchaseUsers(usersResponse?.data?.users || []);
      }

      const [branchesData, classesData, productSuppliers] = await Promise.all([
        suppliesService.getBranches(),
        productsService.getProductClasses(),
        productsService.getProductSuppliers()
      ]);
      setBranches(branchesData);
      setProductClasses(classesData);
      setExistingSuppliers(productSuppliers || []);

      // Load initial orders data
      await loadOrders();
    } catch (error) {
      console.error('Error loading initial data:', error);
      setLoading(false); // Ensure loading state is turned off even if there's an error
    }
  };

  const loadOrders = useCallback(async () => {
    try {
      setLoading(true);
      const result = await suppliesService.getSupplyOrders(filters);
      
      const normalizedOrders: SupplyOrderWithViewed[] = result.orders.map(order => {
        // If is_viewed is null, treat as '111' (all viewed)
        const is_viewed = (order.is_viewed == null || order.is_viewed === '') ? '111' : order.is_viewed;
        return {
          ...order,
          is_viewed,
          item_price: typeof order.item_price === 'number' ? order.item_price : parseFloat(order.item_price as string) || 0,
          total_price: typeof order.total_price === 'number' ? order.total_price : parseFloat(order.total_price as string) || 0,
          quantity: typeof order.quantity === 'number' ? order.quantity : parseInt(order.quantity as string) || 0,
          notesViewed: is_viewed[0] === '1',
          photosViewed: is_viewed[1] === '1',
          followUpsViewed: is_viewed[2] === '1'
        };
      });
    setOrders(normalizedOrders);
    setPagination(result.pagination);
    setLastRefresh(new Date());
    } catch (error) {
      console.error('Error loading orders:', error);
      // Set empty orders array on error to prevent crashes
      setOrders([]);
      setPagination({
        currentPage: 1,
        totalPages: 1,
        totalRecords: 0,
        recordsPerPage: 5
      });
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to load supply orders',
        confirmButtonColor: '#ff6b35'
      });
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // For filters that should apply immediately (not branch/store stock)
  const handleFilterChange = (key: keyof SupplyOrderFilters, value: any) => {
    // Only update pendingFilters for branch/store stock, others update filters directly
    if (key === 'branch_stock_status' || key === 'store_stock_status') {
      setPendingFilters(prev => ({ ...prev, [key]: value }));
    } else {
      if ((key === 'date_from' || key === 'date_to') && value) {
        let newFilters = { ...filters, [key]: value };
        if (key === 'date_from' && filters.date_to && value === filters.date_to) {
          newFilters.date_from = value + 'T00:00:00';
          newFilters.date_to = value + 'T23:59:59';
        } else if (key === 'date_to' && filters.date_from && value === filters.date_from) {
          newFilters.date_from = value + 'T00:00:00';
          newFilters.date_to = value + 'T23:59:59';
        } else {
          newFilters[key] = value;
        }
        setFilters({ ...newFilters, page: 1 });
        return;
      }
      if (filters.limit === 10000) {
        setFilters(prev => ({
          ...prev,
          [key]: value,
          limit: 5,
          page: 1
        }));
      } else {
        setFilters(prev => ({
          ...prev,
          [key]: value,
          page: 1
        }));
      }
    }
  };

  const clearFilters = () => {
    const defaultLimit = 5;
    setFilters({
      page: 1,
      limit: defaultLimit,
      branch_stock_status: undefined,
      store_stock_status: undefined
    });
    setPendingFilters({
      page: 1,
      limit: defaultLimit,
      branch_stock_status: undefined,
      store_stock_status: undefined
    });
    setPagination(prev => ({ ...prev, recordsPerPage: defaultLimit, currentPage: 1 }));
    setViewFilter({
      allViewed: false,
      photosViewed: false,
      notesViewed: false,
      followUpViewed: false,
      allUnViewed: false,
      photosUnViewed: false,
      notesUnViewed: false,
      followUpUnViewed: false
    });
    setPendingViewFilter({
      allViewed: false,
      photosViewed: false,
      notesViewed: false,
      followUpViewed: false,
      allUnViewed: false,
      photosUnViewed: false,
      notesUnViewed: false,
      followUpUnViewed: false
    });
    setViewFilterActive(false);
  };

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
    // Orders will reload due to useEffect below
  };

  const handlePerPageChange = (limit: number) => {
    setFilters(prev => ({ ...prev, limit, page: 1 }));
    // Orders will reload due to useEffect below
  };
  // Reload orders when page or limit changes
  useEffect(() => {
    loadOrders();
  }, [filters.page, filters.limit]);

  const startEditingStatus = (orderId: number, currentStatus: string) => {
    setEditingStatus(prev => ({ ...prev, [orderId]: true }));
    setTempStatus(prev => ({ ...prev, [orderId]: currentStatus }));
  };

  const cancelEditingStatus = (orderId: number) => {
    setEditingStatus(prev => ({ ...prev, [orderId]: false }));
    setTempStatus(prev => {
      const { [orderId]: _, ...rest } = prev;
      return rest;
    });
  };

  const saveStatus = async (orderId: number) => {
    try {
      const newStatus = tempStatus[orderId];
      if (!newStatus) return;

      // If status is being changed to "done", show Swal for supply order number
      if (newStatus === 'done') {
        const { value: supplyOrderNumber } = await Swal.fire({
          title: 'Complete Order - Supply Order Number Required',
          html: `
            <div className="swal2-html-container">
              <p><strong>⚠️ Warning: This action cannot be undone!</strong></p>
              <p>When you mark this order as "Done":</p>
              <ul>
                <li>All photos will be permanently deleted</li>
                <li>Order status will be changed to "Done"</li>
                <li>A supply order number is required to proceed</li>
              </ul>
            </div>
          `,
          input: 'number',
          inputLabel: 'Supply Order Number (Required)',
          inputPlaceholder: 'Enter supply order number',
          inputAttributes: {
            min: '1',
            step: '1'
          },
          showCancelButton: true,
          confirmButtonText: 'Complete Order',
          cancelButtonText: 'Cancel',
          confirmButtonColor: '#28a745',
          cancelButtonColor: '#6c757d',
          inputValidator: (value) => {
            if (!value || isNaN(Number(value)) || Number(value) < 1) {
              return 'Supply order number is required and must be a valid positive number';
            }
          }
        });

        if (supplyOrderNumber) {
          await suppliesService.updateSupplyOrderStatus(
            orderId,
            'done',
            Number(supplyOrderNumber)
          );
          try {
            await suppliesService.clearPhotosOnly(orderId);
          } catch (clearError) {}
          setEditingStatus(prev => ({ ...prev, [orderId]: false }));
          setTempStatus(prev => {
            const { [orderId]: _, ...rest } = prev;
            return rest;
          });
          await loadOrders();
          Swal.fire({ 
            icon: 'success', 
            title: 'Status Updated', 
            text: 'Order marked as done, supply order number saved, and photos cleared.', 
            timer: 2000, 
            showConfirmButton: false 
          });
        }
        return;
      }
      await suppliesService.updateSupplyOrderStatus(orderId, newStatus as 'pending' | 'done' | 'unavailable' | 'limited stock');
      
      // If status is changed to "done", clear photos only (keep follow-ups)
      if (newStatus === 'done') {
        try {
          await suppliesService.clearPhotosOnly(orderId);
        } catch (clearError) {
          console.error('Error clearing photos:', clearError);
        }
      }
      
      // Clear editing state
      setEditingStatus(prev => ({ ...prev, [orderId]: false }));
      setTempStatus(prev => {
        const { [orderId]: _, ...rest } = prev;
        return rest;
      });

      // Refresh data from server to get updated timestamps
      await loadOrders();

      Swal.fire({
        icon: 'success',
        title: 'Status Updated',
        text: newStatus === 'done' ? 
          'Order marked as done and photos cleared' : 
          `Order status changed to ${newStatus}`,
        timer: 2000,
        showConfirmButton: false
      });
    } catch (error) {
      console.error('Error updating status:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to update order status',
        confirmButtonColor: '#ff6b35'
      });
    }
  };

  const handleDeleteOrder = async (orderId: number) => {
    const result = await Swal.fire({
      title: 'Delete Order',
      text: 'Are you sure you want to delete this order? This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, delete'
    });

    if (result.isConfirmed) {
      try {
        await suppliesService.deleteSupplyOrder(orderId);
        setOrders(prev => prev.filter(order => order.id !== orderId));
        
        Swal.fire({
          title: 'Deleted',
          text: 'Order has been deleted successfully.',
          icon: 'success',
          timer: 1500,
          showConfirmButton: false
        });
      } catch (error) {
        console.error('Error deleting order:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to delete order',
          confirmButtonColor: '#ff6b35'
        });
      }
    }
  };

  const startEditingAssignment = (orderId: number, currentAssignedTo?: number) => {
    setEditingAssignment(prev => ({ ...prev, [orderId]: true }));
    setTempAssignments(prev => ({ ...prev, [orderId]: currentAssignedTo || null }));
  };

  const saveAssignment = async (orderId: number) => {
    try {
      const assignedTo = tempAssignments[orderId];
      
      // Call the API to update the assignment
      await suppliesService.updateAssignment(orderId, assignedTo);
      
      // Update local state
      setOrders(prev => prev.map(order => 
        order.id === orderId 
          ? { 
              ...order, 
              assigned_to: assignedTo || undefined,
              assigned_to_name: assignedTo ? purchaseUsers.find(u => u.id === assignedTo)?.username : undefined
            }
          : order
      ));

      setEditingAssignment(prev => ({ ...prev, [orderId]: false }));
      setTempAssignments(prev => {
        const newTemp = { ...prev };
        delete newTemp[orderId];
        return newTemp;
      });

      Swal.fire({
        icon: 'success',
        title: 'Assignment Updated',
        text: assignedTo ? 'Order has been assigned successfully.' : 'Assignment has been removed.',
        timer: 1500,
        showConfirmButton: false
      });

      // Refresh data from server to get updated timestamps and assignments
      await loadOrders();
    } catch (error) {
      console.error('Error updating assignment:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to update assignment',
        confirmButtonColor: '#ff6b35'
      });
    }
  };

  const cancelEditingAssignment = (orderId: number) => {
    setEditingAssignment(prev => ({ ...prev, [orderId]: false }));
    setTempAssignments(prev => {
      const newTemp = { ...prev };
      delete newTemp[orderId];
      return newTemp;
    });
  };

  const startEditingSupplier = (orderId: number, currentSupplier: string | undefined) => {
    setEditingSupplier(prev => ({ ...prev, [orderId]: true }));
    setTempSupplier(prev => ({ ...prev, [orderId]: currentSupplier || '' }));
    setSupplierTab(prev => ({ ...prev, [orderId]: 'existing' }));
    setSupplierSearchTerm(prev => ({ ...prev, [orderId]: '' }));
  };

  const saveSupplier = async (orderId: number) => {
    try {
      const supplier = tempSupplier[orderId];
      
      // Update order with supplier
      await suppliesService.updateSupplier(orderId, supplier);

      // Update local state
      setOrders(prev => prev.map(order => 
        order.id === orderId 
          ? { ...order, supplier: supplier } 
          : order
      ));

      setEditingSupplier(prev => ({ ...prev, [orderId]: false }));
      setTempSupplier(prev => {
        const newTemp = { ...prev };
        delete newTemp[orderId];
        return newTemp;
      });
      setSupplierSearchTerm(prev => {
        const newSearch = { ...prev };
        delete newSearch[orderId];
        return newSearch;
      });

      Swal.fire({
        icon: 'success',
        title: 'Supplier Updated',
        text: 'Supplier has been updated successfully.',
        timer: 1500,
        showConfirmButton: false
      });

      // Refresh data from server to get updated timestamps
      await loadOrders();
    } catch (error) {
      console.error('Error updating supplier:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to update supplier',
        confirmButtonColor: '#ff6b35'
      });
    }
  };

  const cancelEditingSupplier = (orderId: number) => {
    setEditingSupplier(prev => ({ ...prev, [orderId]: false }));
    setTempSupplier(prev => {
      const newTemp = { ...prev };
      delete newTemp[orderId];
      return newTemp;
    });
    setSupplierSearchTerm(prev => {
      const newSearch = { ...prev };
      delete newSearch[orderId];
      return newSearch;
    });
  };

  const showPhotos = (photos: string[], orderId?: number) => {
    setPhotoModal({
      show: true,
      photos,
      currentIndex: 0,
      orderId
    });
  };

  const showFollowUps = async (orderId: number) => {
    const order = orders.find(o => o.id === orderId);
    if (order) {
      setFollowUpModal({
        show: true,
        orderId,
        followUps: order.follow_ups || [],
        isAdding: false,
        newNotes: '',
        newPhotos: [],
        newStatus: ''
      });
    }
  };

  const addFollowUp = async () => {
    if (!followUpModal.orderId || !followUpModal.newNotes.trim()) {
      Swal.fire({
        icon: 'warning',
        title: 'Notes Required',
        text: 'Please enter follow-up notes',
        confirmButtonColor: '#ff6b35'
      });
      return;
    }

    try {
      await suppliesService.addFollowUp(
        followUpModal.orderId,
        followUpModal.newNotes,
        followUpModal.newPhotos,
        followUpModal.newStatus
      );

      // Reset modal state
      setFollowUpModal(prev => ({
        ...prev,
        isAdding: false,
        newNotes: '',
        newPhotos: [],
        newStatus: ''
      }));

      // Refresh data from server to get updated timestamps and follow-ups
      await loadOrders();

      Swal.fire({
        icon: 'success',
        title: 'Follow-up Added',
        text: 'Follow-up has been added successfully',
        timer: 1500,
        showConfirmButton: false
      });
    } catch (error) {
      console.error('Error adding follow-up:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to add follow-up',
        confirmButtonColor: '#ff6b35'
      });
    }
  };

  const PhotoUploadComponent: React.FC<{ onPhotosAdded: (photos: string[]) => void }> = ({ onPhotosAdded }) => {
    const fileInputRef = React.useRef<HTMLInputElement>(null);

    const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (!files || files.length === 0) return;

      const validFiles: File[] = [];
      
      // First, validate all files and collect valid ones
      Array.from(files).forEach(file => {
        // Validate file type
        if (!file.type.startsWith('image/')) {
          Swal.fire({
            icon: 'warning',
            title: 'Invalid File Type',
            text: `File "${file.name}" is not a valid image file.`,
            confirmButtonColor: '#ff6b35'
          });
          return;
        }

        // Validate file size (10MB limit)
        if (file.size > 10485760) {
          Swal.fire({
            icon: 'warning',
            title: 'File Too Large',
            text: `File "${file.name}" is larger than 10MB limit.`,
            confirmButtonColor: '#ff6b35'
          });
          return;
        }

        validFiles.push(file);
      });

      if (validFiles.length === 0) {
        return;
      }

      const photosToAdd: string[] = [];
      let filesProcessed = 0;

      // Process valid files
      validFiles.forEach((file) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const base64 = e.target?.result as string;
          
          if (base64) {
            // Create an image to get dimensions and compress if needed
            const img = new Image();
            img.onload = () => {
              const canvas = document.createElement('canvas');
              const ctx = canvas.getContext('2d');
              
              // Set maximum dimensions (compress large images)
              const MAX_WIDTH = 800;
              const MAX_HEIGHT = 600;
              let { width, height } = img;
              
              // Calculate new dimensions
              if (width > height) {
                if (width > MAX_WIDTH) {
                  height = height * (MAX_WIDTH / width);
                  width = MAX_WIDTH;
                }
              } else {
                if (height > MAX_HEIGHT) {
                  width = width * (MAX_HEIGHT / height);
                  height = MAX_HEIGHT;
                }
              }
              
              // Set canvas dimensions and draw compressed image
              canvas.width = width;
              canvas.height = height;
              ctx?.drawImage(img, 0, 0, width, height);
              
              // Get compressed base64 (with reduced quality)
              const compressedBase64 = canvas.toDataURL('image/jpeg', 0.7);
              photosToAdd.push(compressedBase64);
              
              filesProcessed++;
              
              // When all valid files are processed, call onPhotosAdded
              if (filesProcessed === validFiles.length) {
                onPhotosAdded(photosToAdd);
              }
            };
            img.src = base64;
          } else {
            filesProcessed++;
            
            // Still check if all files are processed even if base64 is empty
            if (filesProcessed === validFiles.length && photosToAdd.length > 0) {
              onPhotosAdded(photosToAdd);
            }
          }
        };
        
        reader.onerror = (error) => {
          console.error('Error reading file:', file.name, error);
          filesProcessed++;
          
          // Still check if all files are processed even on error
          if (filesProcessed === validFiles.length && photosToAdd.length > 0) {
            onPhotosAdded(photosToAdd);
          }
        };
        
        reader.readAsDataURL(file);
      });

      // Reset the input value
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    };

    const handleUploadClick = () => {
      if (fileInputRef.current) {
        fileInputRef.current.click();
      }
    };

    return (
      <div className="photo-dropzone" onClick={handleUploadClick}>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleFileInputChange}
        />
        <i className="bi bi-cloud-upload"></i>
        <p className="mb-0">Click to select photos</p>
      </div>
    );
  };

  const exportToExcel = async () => {
    try {
      setExporting(true);
      
      // Get all orders without pagination for export
      const allOrdersResult = await suppliesService.getSupplyOrders({
        ...filters,
        page: undefined,
        limit: undefined
      });

      const exportData = allOrdersResult.orders.map(order => ({
        'ID': order.id,
        'BRANCH': order.branch?.branch || '--',
        'CALL CENTER': (order as any).call_center === 1 ? 'Call Center' : (order as any).call_center === 2 ? 'Social Media' : 'Branch',
        'DATE': new Date(order.order_date).toLocaleDateString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: true
        }),
        'ITEM CODE': order.item_code,
        'ITEM NAME': order.product?.item_name || order.item_name,
        'QTY': order.quantity,
        'PRICE': order.item_price,
        'TOTAL': order.total_price,
        'PRODUCT CLASS': order.prod_class ||'--',
        'NOTES': order.notes ||'--',
        'PHOTOS': order.photos?.length ? `${order.photos.length} photo(s)` : 'No photos',
        'LAST FOLLOW UP NAME': order.follow_ups?.length ? order.follow_ups[order.follow_ups.length - 1].user_name :'--',
        'LAST FOLLOW UP DATE': order.follow_ups?.length ? 
          new Date(order.follow_ups[order.follow_ups.length - 1].created_at).toLocaleDateString('en-GB') :'--',
        'STOCK': order.stock_info?.stock_status === 'in stock' ? 'In Stock' : 'Out of Stock',
        'STATUS': order.status.toUpperCase(),
        'UPDATED NAME': order.updated_by_name ||'--',
        'UPDATED TIME': new Date(order.updated_at).toLocaleDateString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: true
        }),
      }));

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Supply Orders');

      // Add some basic styling
      const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const cellAddress = XLSX.utils.encode_cell({ r: 0, c: C });
        if (!worksheet[cellAddress]) continue;
        worksheet[cellAddress].s = {
          font: { bold: true },
          fill: { fgColor: { rgb: 'FF6B35' } },
          alignment: { horizontal: 'center' }
        };
      }

      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
      const fileName = `supply_orders_${new Date().toISOString().split('T')[0]}.xlsx`;
      saveAs(blob, fileName);

      Swal.fire({
        icon: 'success',
        title: 'Export Successful',
        text: 'Supply orders have been exported to Excel',
        timer: 1500,
        showConfirmButton: false
      });
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      Swal.fire({
        icon: 'error',
        title: 'Export Failed',
        text: 'Failed to export supply orders',
        confirmButtonColor: '#ff6b35'
      });
    } finally {
      setExporting(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const getLastFollowUp = (order: SupplyOrder) => {
    if (!order.follow_ups || order.follow_ups.length === 0) {
      return { name:'--', date:'--' };
    }
    
    const lastFollowUp = order.follow_ups[order.follow_ups.length - 1];
    return {
      name: lastFollowUp.user_name,
      date: formatDate(lastFollowUp.created_at)
    };
  };

  const [filtersCollapsed, setFiltersCollapsed] = React.useState(false);

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-orange" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="text-muted mt-2">Loading supply orders...</p>
      </div>
    );
  }
  return (
    <>
      {/* Main Content Container */}
      <div className="main-container">
        <div className="content-area">
          
          {/* Filters Toggle Arrow */}
  <div className="filter-controls" ref={filtersRef}>
          <div className="filter-controls-header">
            <button
              type="button"
              className="btn btn-light btn-sm filter-controls-toggle"
              onClick={() => setFiltersCollapsed((prev) => !prev)}
              aria-label={filtersCollapsed ? 'Expand filters' : 'Collapse filters'}
            >
              <i className={`bi ${filtersCollapsed ? 'bi-chevron-down' : 'bi-chevron-up'}`}></i>
            </button>
            <span className="filter-controls-label">Filters</span>
          </div>
          
          {/* Filters */}
          <div className={`filter-controls filter-controls-content${filtersCollapsed ? ' collapsed' : ''}`}> 
            <div
              className={`row g-3${(photoUploadModal.show || followUpModal.show || photoModal.show || notesModal?.show) ? ' supply-orders-filters-blur' : ''}`}
            >
              <div className="col-md-3 supply-orders-rtl-left">
                <label className="form-label">Branch</label>
                <select
                  className="form-select form-select-sm supply-orders-rtl-right"
                  value={filters.branch_id || ''}
                  onChange={(e) => handleFilterChange('branch_id', e.target.value ? parseInt(e.target.value) : undefined)}
                >
                  <option value="" className="supply-orders-rtl-left">All Branches</option>
                  {branches.map(branch => (
                    <option key={branch.branch_id} value={branch.branch_id}>
                      {branch.branch}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-1">
                <label className="form-label">Date From</label>
                  <DatePicker
                    selected={filters.date_from ? new Date(filters.date_from) : null}
                    onChange={(date) => handleFilterChange('date_from', date ? date.toLocaleDateString('en-CA') : undefined)}
                    dateFormat="dd/MM/yyyy"
                    className="form-control form-control-sm"
                    placeholderText="Select date"
                    isClearable
                  />
              </div>

              <div className="col-md-1">
                <label className="form-label">Date To</label>
                  <DatePicker
                    selected={filters.date_to ? new Date(filters.date_to) : null}
                    onChange={(date) => handleFilterChange('date_to', date ? date.toLocaleDateString('en-CA') : undefined)}
                    dateFormat="dd/MM/yyyy"
                    className="form-control form-control-sm"
                    placeholderText="Select date"
                    isClearable
                  />
              </div>

              <div className="col-md-2">
                <label className="form-label">Item Code</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  value={filters.item_code || ''}
                  onChange={(e) => handleFilterChange('item_code', e.target.value)}
                  placeholder="Search code"
                />
              </div>

              <div className="col-md-3">
                <label className="form-label">Item Name</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  value={filters.item_name || ''}
                  onChange={(e) => handleFilterChange('item_name', e.target.value)}
                  placeholder="Search name"
                />
              </div>

              <div className="col-md-2">
                <label className="form-label">Status</label>
                <select
                  className="form-select form-select-sm"
                  value={filters.status || ''}
                  onChange={(e) => handleFilterChange('status', e.target.value || undefined)}
                >
                  <option value="">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="done">Done</option>
                  <option value="unavailable">Unavailable</option>
                  <option value="limited stock">Limited Stock</option>
                </select>
              </div>

              <div className="col-md-2">
                <label className="form-label">Product Class</label>
                <select
                  className="form-select form-select-sm"
                  value={filters.prod_class || ''}
                  onChange={(e) => handleFilterChange('prod_class', e.target.value || undefined)}
                >
                  <option value="">All Classes</option>
                  {productClasses.map(cls => (
                    <option key={cls} value={cls}>{cls}</option>
                  ))}
                </select>
              </div>

              <div className="col-md-2">
                <label className="form-label">Branch Stock</label>
                <select
                  className="form-select form-select-sm"
                  value={pendingFilters.branch_stock_status || ''}
                  onChange={(e) => handleFilterChange('branch_stock_status', e.target.value || undefined)}
                >
                  <option value="">All Branch Stock</option>
                  <option value="in stock">In Stock</option>
                  <option value="out of stock">Out of Stock</option>
                </select>
              </div>
              
              <div className="col-md-2">
                <label className="form-label">Store Stock</label>
                <select
                  className="form-select form-select-sm"
                  value={pendingFilters.store_stock_status || ''}
                  onChange={(e) => handleFilterChange('store_stock_status', e.target.value || undefined)}
                >
                  <option value="">All Store Stock</option>
                  <option value="in stock">In Stock</option>
                  <option value="out of stock">Out of Stock</option>
                </select>
              </div>

              {canAssignOrders() && (
                <div className="col-md-2">
                  <label className="form-label">Assigned To</label>
                  <select
                    className="form-select form-select-sm"
                    value={filters.assigned_to || ''}
                    onChange={(e) => handleFilterChange('assigned_to', e.target.value ? parseInt(e.target.value) : undefined)}
                  >
                    <option value="">All Users</option>
                    {purchaseUsers.map(user => (
                      <option key={user.id} value={user.id}>
                        {user.username}
                      </option>
                    ))}
                  </select>
                </div>
              )}

                {/* View Filter Dropdown */}
                <div className="col-md-2">
                  <label className="form-label">View</label>
                  <div className="dropdown position-relative">
                    <button
                      className="form-select form-select-sm d-flex justify-content-between align-items-center supply-orders-btn-fullwidth"
                      type="button"
                      onClick={() => setViewDropdownOpen((open) => !open)}
                    >
                      {(() => {
                        return pendingViewFilter.allViewed
                          ? 'All Viewed'
                          : pendingViewFilter.allUnViewed
                          ? 'All UnViewed'
                          : [
                              pendingViewFilter.photosViewed && 'Photos Viewed',
                              pendingViewFilter.notesViewed && 'Notes Viewed',
                              pendingViewFilter.followUpViewed && 'Follow Up Viewed',
                              pendingViewFilter.photosUnViewed && 'Photos UnViewed',
                              pendingViewFilter.notesUnViewed && 'Notes UnViewed',
                              pendingViewFilter.followUpUnViewed && 'Follow Up UnViewed'
                            ].filter(Boolean).join(', ') || 'View';
                      })()}
                    </button>
                    {viewDropdownOpen && (
                      <div className="dropdown-menu show p-2 supply-orders-dropdown-menu">
                        <div className="mb-2 fw-bold">Viewed</div>
                        <div className="form-check">
                          <input className="form-check-input" type="radio" name="viewedGroup" id="allViewed" checked={pendingViewFilter.allViewed} onChange={() => handleViewFilterChange('allViewed', true)} />
                          <label className="form-check-label" htmlFor="allViewed">All Viewed</label>
                        </div>
                        <div className="ms-3">
                          <div className="form-check">
                            <input className="form-check-input" type="checkbox" id="photosViewed" checked={pendingViewFilter.photosViewed} disabled={pendingViewFilter.allViewed} onChange={() => handleViewFilterChange('photosViewed', !pendingViewFilter.photosViewed)} />
                            <label className="form-check-label" htmlFor="photosViewed">Photos Viewed</label>
                          </div>
                          <div className="form-check">
                            <input className="form-check-input" type="checkbox" id="notesViewed" checked={pendingViewFilter.notesViewed} disabled={pendingViewFilter.allViewed} onChange={() => handleViewFilterChange('notesViewed', !pendingViewFilter.notesViewed)} />
                            <label className="form-check-label" htmlFor="notesViewed">Notes Viewed</label>
                          </div>
                          <div className="form-check">
                            <input className="form-check-input" type="checkbox" id="followUpViewed" checked={pendingViewFilter.followUpViewed} disabled={pendingViewFilter.allViewed} onChange={() => handleViewFilterChange('followUpViewed', !pendingViewFilter.followUpViewed)} />
                            <label className="form-check-label" htmlFor="followUpViewed">Follow Up Viewed</label>
                          </div>
                        </div>
                        <div className="mt-2 fw-bold">UnViewed</div>
                        <div className="form-check">
                          <input className="form-check-input" type="radio" name="viewedGroup" id="allUnViewed" checked={pendingViewFilter.allUnViewed} onChange={() => handleViewFilterChange('allUnViewed', true)} />
                          <label className="form-check-label" htmlFor="allUnViewed">All UnViewed</label>
                        </div>
                        <div className="ms-3">
                          <div className="form-check">
                            <input className="form-check-input" type="checkbox" id="photosUnViewed" checked={pendingViewFilter.photosUnViewed} disabled={pendingViewFilter.allUnViewed} onChange={() => handleViewFilterChange('photosUnViewed', !pendingViewFilter.photosUnViewed)} />
                            <label className="form-check-label" htmlFor="photosUnViewed">Photos UnViewed</label>
                          </div>
                          <div className="form-check">
                            <input className="form-check-input" type="checkbox" id="notesUnViewed" checked={pendingViewFilter.notesUnViewed} disabled={pendingViewFilter.allUnViewed} onChange={() => handleViewFilterChange('notesUnViewed', !pendingViewFilter.notesUnViewed)} />
                            <label className="form-check-label" htmlFor="notesUnViewed">Notes UnViewed</label>
                          </div>
                          <div className="form-check">
                            <input className="form-check-input" type="checkbox" id="followUpUnViewed" checked={pendingViewFilter.followUpUnViewed} disabled={pendingViewFilter.allUnViewed} onChange={() => handleViewFilterChange('followUpUnViewed', !pendingViewFilter.followUpUnViewed)} />
                            <label className="form-check-label" htmlFor="followUpUnViewed">Follow Up UnViewed</label>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              <div className="col-md-3">
                <label className="form-label">Has Follow Up</label>
                <select
                  className="form-select form-select-sm"
                  value={filters.hasFollowUp || ''}
                  onChange={(e) => handleFilterChange('hasFollowUp', e.target.value)}
                >
                  <option value="">Select</option>
                  <option value="yes">Has Follow Up</option>
                  <option value="no">Has No Follow Up</option>
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label">Per Page</label>
                <select
                  className="form-select form-select-sm"
                  value={filters.limit || 5}
                  onChange={(e) => handlePerPageChange(parseInt(e.target.value))}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={50000}>Show All Rows</option>
                </select>
              </div>

                <div className="col-md-2">
                  <label className="form-label">Assigned Supplier</label>
                  <div className="supplier-search-dropdown">
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={filters.supplier || ''}
                      onChange={(e) => handleFilterChange('supplier', e.target.value)}
                      placeholder="Search supplier"
                      list="supplier-options"
                    />
                    <datalist id="supplier-options">
                      <option value="">All Suppliers</option>
                      {existingSuppliers.map((supplier, idx) => (
                        <option key={idx} value={supplier}>
                          {supplier}
                        </option>
                      ))}
                    </datalist>
                  </div>
                </div>
              
              <div className="col-md-4 d-flex align-items-end">
                <div className="export-controls">
                  <button
                    type="button"
                    className="btn btn-outline-danger btn-sm"
                    onClick={clearFilters}
                  >
                    <i className="bi bi-x-circle me-1"></i>
                    Clear Filters
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-success btn-sm"
                    onClick={() => {
                      // Use pendingViewFilter and pendingFilters to apply all filters on refresh
                      // Build is_viewed list covering all combinations where the selected bit is set/unset
                      let viewedFilter: string[] | undefined = undefined;
                      if (pendingViewFilter.allViewed) {
                        viewedFilter = ['111'];
                      } else if (pendingViewFilter.photosViewed) {
                        // any value where middle digit (photos) === '1'
                        viewedFilter = ['010','011','110','111'];
                      } else if (pendingViewFilter.notesViewed) {
                        // any value where first digit (notes) === '1'
                        viewedFilter = ['100','101','110','111'];
                      } else if (pendingViewFilter.followUpViewed) {
                        // any value where third digit (follow_up) === '1'
                        viewedFilter = ['001','011','101','111'];
                      } else if (pendingViewFilter.allUnViewed) {
                        viewedFilter = ['000'];
                      } else if (pendingViewFilter.photosUnViewed) {
                        // any value where middle digit (photos) === '0'
                        viewedFilter = ['000','001','100','101'];
                      } else if (pendingViewFilter.notesUnViewed) {
                        // any value where first digit (notes) === '0'
                        viewedFilter = ['000','001','010','011'];
                      } else if (pendingViewFilter.followUpUnViewed) {
                        // any value where third digit (follow_up) === '0'
                        viewedFilter = ['000','010','100','110'];
                      }

                      setViewFilter(pendingViewFilter);
                      setViewFilterActive(!!viewedFilter);
                      // Apply pendingFilters to real filters
                      const newFilters = {
                        ...filters,
                        ...pendingFilters,
                        limit: pendingFilters.limit || filters.limit || 5,
                        page: 1,
                        is_viewed: viewedFilter
                      };
                      setFilters(newFilters);
                      (async () => {
                        try {
                          setLoading(true);
                          const collected: any[] = [];
                          // Fetch first page
                          // Send hasFollowUp to backend so server filters rows with/without follow-ups
                          const backendFilters: any = { ...newFilters };
                          const first = await suppliesService.getSupplyOrders(backendFilters as any);
                          // Fallback: if server returned no rows when requesting hasFollowUp, try fetching without that filter and apply client-side
                          let firstResponse = first;
                          if (backendFilters.hasFollowUp && (!first.orders || first.orders.length === 0)) {
                            const fallbackFilters = { ...backendFilters };
                            delete fallbackFilters.hasFollowUp;
                            const fallbackFirst = await suppliesService.getSupplyOrders(fallbackFilters as any);
                            firstResponse = fallbackFirst;
                          }
                          const normalize = (orders: any[]) => orders.map((order: any) => {
                            const is_viewed = (order.is_viewed == null || order.is_viewed === '') ? '111' : order.is_viewed;
                            return {
                              ...order,
                              is_viewed,
                              item_price: typeof order.item_price === 'number' ? order.item_price : parseFloat(order.item_price as string) || 0,
                              total_price: typeof order.total_price === 'number' ? order.total_price : parseFloat(order.total_price as string) || 0,
                              quantity: typeof order.quantity === 'number' ? order.quantity : parseInt(order.quantity as string) || 0,
                              notesViewed: is_viewed[0] === '1',
                              photosViewed: is_viewed[1] === '1',
                              followUpsViewed: is_viewed[2] === '1'
                            };
                          });

                          collected.push(...(firstResponse.orders || []));

                          // If server returned fewer rows than the selected limit and there are more pages, fetch subsequent pages
                          const desired = newFilters.limit || 5;
                          let currentPage = first.pagination.currentPage || 1;
                          const totalPages = first.pagination.totalPages || 1;
                          while (collected.length < desired && currentPage < totalPages) {
                            currentPage += 1;
                            const nextBackendFilters = { ...backendFilters, page: currentPage };
                            const next = await suppliesService.getSupplyOrders(nextBackendFilters as any);
                            if (!next.orders || next.orders.length === 0) break;
                            collected.push(...next.orders);
                          }

                          const normalizedCollected = normalize(collected).slice(0, desired);
                          setOrders(normalizedCollected);
                          // Use pagination from the first response (represents filtered dataset)
                          setPagination(first.pagination);
                          setLastRefresh(new Date());
                        } catch (error: any) {
                          console.error(error);
                          setOrders([]);
                          setPagination({
                            currentPage: 1,
                            totalPages: 1,
                            totalRecords: 0,
                            recordsPerPage: 5
                          });
                          Swal.fire({
                            icon: 'error',
                            title: 'Error',
                            text: error?.message || 'Failed to fetch supply orders',
                            confirmButtonColor: '#ff6b35'
                          });
                        } finally {
                          setLoading(false);
                        }
                      })();
                    }}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-1"></span>
                        Refreshing...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-arrow-clockwise me-1"></i>
                        Refresh
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    className="btn export-excel-btn btn-sm"
                    onClick={exportToExcel}
                    disabled={exporting}
                  >
                    {exporting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-1"></span>
                        Exporting...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-file-earmark-excel me-1"></i>
                        Export Excel
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Orders Table Info */}
          {/* Removed table info from above table, will be placed in footer */}
        </div>
        
        {/* Orders Table Container with adjusted height */}
        <div className={`supply-orders-table-container supply-orders-table-main${!filtersCollapsed ? ' filters-expanded' : ''}`}>
          <div className="supply-orders-table-container supply-orders-table-relative">
            <table className="table table-striped table-hover supply-orders-table-layout">
            <thead className="sticky-header">
                <tr className="text-truncate">
                  <th>Check box</th>
                  <th style={{cursor: 'pointer'}} onClick={() => handleHeaderSort('id')}>ID {renderSortIcon('id')}</th>
                  <th style={{cursor: 'pointer'}} onClick={() => handleHeaderSort('branch')}>BRANCH {renderSortIcon('branch')}</th>
                  <th style={{cursor: 'pointer'}} onClick={() => handleHeaderSort('created_by')}>CREATED BY {renderSortIcon('created_by')}</th>
                  <th style={{cursor: 'pointer'}} onClick={() => handleHeaderSort('call_center')}>CATEGORY {renderSortIcon('call_center')}</th>
                  <th style={{cursor: 'pointer'}} onClick={() => handleHeaderSort('order_date')}>DATE {renderSortIcon('order_date')}</th>
                  <th style={{cursor: 'pointer'}} onClick={() => handleHeaderSort('item_code')}>ITEM CODE {renderSortIcon('item_code')}</th>
                  <th className="supply-orders-item-name-header" style={{cursor: 'pointer'}} onClick={() => handleHeaderSort('item_name')}>ITEM NAME {renderSortIcon('item_name')}</th>
                  <th style={{cursor: 'pointer'}} onClick={() => handleHeaderSort('quantity')}>QTY {renderSortIcon('quantity')}</th>
                  <th style={{cursor: 'pointer'}} onClick={() => handleHeaderSort('item_price')}>PRICE {renderSortIcon('item_price')}</th>
                  <th style={{cursor: 'pointer'}} onClick={() => handleHeaderSort('total_price')}>TOTAL {renderSortIcon('total_price')}</th>
                  {canManageSuppliers() && user && user.role !== 'Supplies agent' && (
                    <>
                      <th style={{cursor: 'pointer'}} onClick={() => handleHeaderSort('supplier')}>SUPPLIER {renderSortIcon('supplier')}</th>
                    </>
                  )}
                    <th style={{cursor: 'pointer'}} onClick={() => handleHeaderSort('supplier')}>ASSIGNED SUPPLIER {renderSortIcon('supplier')}</th>                  
                  <th style={{cursor: 'pointer'}} onClick={() => handleHeaderSort('prod_class')}>PRODUCT CLASS {renderSortIcon('prod_class')}</th>
                  <th style={{cursor: 'pointer'}} onClick={() => handleHeaderSort('notes_count')}>NOTES {renderSortIcon('notes_count')}</th>
                  <th style={{cursor: 'pointer'}} onClick={() => handleHeaderSort('photos_count')}>PHOTOS {renderSortIcon('photos_count')}</th>
                  <th style={{cursor: 'pointer'}} onClick={() => handleHeaderSort('followups_count')}>FOLLOW UP {renderSortIcon('followups_count')}</th>
                  <th style={{cursor: 'pointer'}} onClick={() => handleHeaderSort('last_followup_name')}>LAST FOLLOW UP NAME {renderSortIcon('last_followup_name')}</th>
                  <th style={{cursor: 'pointer'}} onClick={() => handleHeaderSort('last_followup_date')}>LAST FOLLOW UP DATE {renderSortIcon('last_followup_date')}</th>
                  <th style={{cursor: 'pointer'}} onClick={() => handleHeaderSort('branch_stock_status')}>BRANCH STOCK {renderSortIcon('branch_stock_status')}</th>
                  <th style={{cursor: 'pointer'}} onClick={() => handleHeaderSort('store_stock_status')}>STORE STOCK {renderSortIcon('store_stock_status')}</th>
                  {canAssignOrders() && (
                    <>
                      <th>ASSIGN TO</th>
                      <th style={{cursor: 'pointer'}} onClick={() => handleHeaderSort('assigned_to_name')}>ASSIGNED TO {renderSortIcon('assigned_to_name')}</th>
                    </>
                  )}
                  <th style={{cursor: 'pointer'}} onClick={() => handleHeaderSort('status')}>STATUS {renderSortIcon('status')}</th>
                  <th style={{cursor: 'pointer'}} onClick={() => handleHeaderSort('updated_by_name')}>UPDATED NAME {renderSortIcon('updated_by_name')}</th>
                  <th style={{cursor: 'pointer'}} onClick={() => handleHeaderSort('updated_at')}>UPDATED TIME {renderSortIcon('updated_at')}</th>
                  <th>ACTIONS</th>
                  <th style={{cursor: 'pointer'}} onClick={() => handleHeaderSort('supply_order_number')}>Supply Order Numbers {renderSortIcon('supply_order_number')}</th>
                </tr>
                </thead>
              <tbody>
                {getFilteredOrders().length === 0 ? (
                  <tr>
                    <td colSpan={canAssignOrders() && canManageSuppliers() ? 24 : (canAssignOrders() || canManageSuppliers() ? 22 : 20)} className="text-center py-5">
                      <div className="empty-state">
                        <i className="bi bi-inbox"></i>
                        <p>No supply orders found</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  getFilteredOrders().map((order) => {
                    const isSelected = isOrderSelected(order.id);
                    return (
                      <tr 
                        className={`text-truncate ${isSelected ? 'table-row-selected' : ''}`} 
                        key={order.id}
                      >
                        <td>
                          <input 
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleSelectOrder(order.id)}
                          />
                        </td>
                        <td>{order.id}</td>
                        <td className='supply-orders-branch'>{order.branch?.branch ||'--'}</td>
                        <td>{order.created_by || '--'}</td>
                        <td className="text-truncate">
                          <span 
                            className={`badge ${order.call_center === 1 ? 'bg-success' : order.call_center === 2 ? 'bg-info' : 'bg-danger'} supply-orders-badge-font`}
                          >
                            {order.call_center === 1 ? 'Call Center' : order.call_center === 2 ? 'Social Media' : 'Branch'}
                          </span>
                        </td>
                        <td>{formatDate(order.order_date)}</td>
                        <td className="text-truncate" >{order.item_code}</td>
                        <td className="text-truncate supply-orders-item-name-cell" title={order.product?.item_name || order.item_name}>
                          {order.product?.item_name || order.item_name}
                        </td>
                        <td className="text-truncate">{order.quantity}</td>
                        <td>${typeof order.item_price === 'number' ? order.item_price.toFixed(2) : '0.00'}</td>
                        <td>${typeof order.total_price === 'number' ? order.total_price.toFixed(2) : '0.00'}</td>
                        {canManageSuppliers() && user && user.role !== 'Supplies agent' && (
                          <>
                            {/* SUPPLIER Column */}
                            <td>
                              {editingSupplier[order.id] ? (
                                <div className="supplier-edit-container">
                                  {supplierTab[order.id] === 'existing' ? (
                                    <div className="supplier-selection">
                                      <div className="d-flex">
                                        <div className="me-1 supply-orders-minwidth-180">
                                          <input
                                            type="text"
                                            className="form-control form-control-sm mb-1 supply-orders-width-75"
                                            placeholder="Search..."
                                            value={supplierSearchTerm[order.id] || ''}
                                            onChange={(e) => setSupplierSearchTerm(prev => ({
                                              ...prev,
                                              [order.id]: e.target.value
                                            }))}
                                          />
                                          <select
                                            className="form-select form-select-sm supply-orders-width-75"
                                            value={tempSupplier[order.id] || ''}
                                            onChange={(e) => setTempSupplier(prev => ({
                                              ...prev,
                                              [order.id]: e.target.value
                                            }))}
                                          >
                                            <option value="">Select</option>
                                            {existingSuppliers
                                              .filter(supplier => 
                                                !supplierSearchTerm[order.id] || 
                                                supplier.toLowerCase().includes(supplierSearchTerm[order.id]?.toLowerCase() || '')
                                              )
                                              .map((supplier, idx) => (
                                                <option key={idx} value={supplier}>
                                                  {supplier}
                                                </option>
                                              ))
                                            }
                                          </select>
                                          <div className="d-flex mt-1">     
                                             <button
                                              className="btn btn-success btn-sm me-2 px-4 py-0 supply-orders-font-08"
                                              onClick={() => saveSupplier(order.id)}
                                              title="Assign supplier"
                                            >
                                              <i className="bi bi-check"></i>
                                            </button>
                                            <button
                                              className="btn btn-secondary btn-sm me-2 px-4 py-0 supply-orders-font-08"
                                              onClick={() => cancelEditingSupplier(order.id)}
                                              title="Cancel"
                                            >
                                              <i className="bi bi-x"></i>
                                            </button>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="input-group input-group-sm">
                                      <input
                                        type="text"
                                        className="form-control form-control-sm"
                                        value={tempSupplier[order.id] || ''}
                                        onChange={(e) => setTempSupplier(prev => ({
                                          ...prev,
                                          [order.id]: e.target.value
                                        }))}
                                        placeholder="Enter supplier name"
                                      />
                                      <button
                                        className="btn btn-success btn-sm"
                                        onClick={() => saveSupplier(order.id)}
                                        title="Save supplier"
                                      >
                                        <i className="bi bi-check"></i>
                                      </button>
                                      <button
                                        className="btn btn-secondary btn-sm"
                                        onClick={() => cancelEditingSupplier(order.id)}
                                        title="Cancel"
                                      >
                                        <i className="bi bi-x"></i>
                                      </button>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div className="d-flex gap-1">
                                  <button
                                    className="btn btn-outline-primary btn-sm"
                                    onClick={() => {
                                      startEditingSupplier(order.id, order.supplier);
                                      setSupplierTab(prev => ({ ...prev, [order.id]: 'existing' }));
                                    }}
                                    title="Choose existing supplier"
                                  >
                                    <i className="bi bi-list"></i> Choose
                                  </button>
                                  <button
                                    className="btn btn-outline-success btn-sm"
                                    onClick={() => {
                                      startEditingSupplier(order.id, order.supplier);
                                      setSupplierTab(prev => ({ ...prev, [order.id]: 'new' }));
                                    }}
                                    title="Add new supplier"
                                  >
                                    <i className="bi bi-plus"></i> Add New
                                  </button>
                                </div>
                              )}
                            </td>
                          </>
                        )}
                        {/* ASSIGNED SUPPLIER Column */}
                        <td className="text-truncate">
                          {order.supplier ? (
                            <span className="badge bg-info text-dark">
                              {order.supplier}
                            </span>
                          ) : (
                            <span className="text-muted">Not assigned</span>
                          )}
                        </td>                        
                        <td className="text-truncate">{order.prod_class ||'--'}</td>
                        <td>
                          <div className="d-flex gap-1 notes-actions">
                            <button
                              className="btn btn-outline-success btn-sm"
                              onClick={() => setNotesModal({
                                show: true,
                                orderId: order.id,
                                notes: order.notes ? order.notes.split('\n---\n').map(note => {
                                  // Try to parse note as JSON, fallback to string
                                  try {
                                    const obj = JSON.parse(note);
                                    if (obj && obj.text && obj.timestamp) return obj;
                                  } catch {
                                    // fallback for legacy notes
                                  }
                                  return { text: note, timestamp: 'Unknown' };
                                }) : [],
                                newNote: '',
                                isAdding: true
                              })}
                            >
                              <i className="bi bi-plus"></i> Add
                            </button>
                              <button
                                className={`btn btn-sm ${order.notesViewed ? 'btn-primary' : 'btn-danger'}`}
                                disabled={!(order.notes && order.notes.length > 0)}
                                onClick={async () => {
                                  setNotesModal({
                                    show: true,
                                    orderId: order.id,
                                    notes: order.notes ? order.notes.split('\n---\n').map(note => {
                                      try {
                                        const obj = JSON.parse(note);
                                        if (obj && obj.text && obj.timestamp) return obj;
                                      } catch {}
                                      return { text: note, timestamp: 'Unknown' };
                                    }) : [],
                                    newNote: '',
                                    isAdding: false
                                  });
                                  if (!order.notesViewed) {
                                    try {
                                      const is_viewed = await suppliesService.updateIsViewed(order.id, 'notes');
                                      setOrders(prev => prev.map(o => o.id === order.id ? { ...o, notesViewed: true, is_viewed } : o));
                                    } catch (err) {
                                      alert('Failed to mark Notes as viewed.');
                                      console.error(err);
                                    }
                                  }
                                }}
                              >
                                <i className="bi bi-eye"></i> {order.notesViewed ? 'Viewed' : 'View'} ({order.notes ? order.notes.split('\n---\n').length : 0})
                              </button>
                          </div>
                        </td>
                        <td>
                            <div className="d-flex gap-1 notes-actions">
                              <button
                                className="btn btn-outline-success btn-sm"
                                onClick={() => setPhotoUploadModal({ show: true, orderId: order.id })}
                              >
                                <i className="bi bi-plus"></i> Add
                              </button>
                              <button
                                className={`btn btn-sm ${(order.photos && order.photos.length > 0) ? (order.photosViewed ? 'btn-primary' : 'btn-danger') : 'btn-danger'}`}
                                disabled={!(order.photos && order.photos.length > 0)}
                                onClick={async () => {
                                  showPhotos(order.photos!, order.id);
                                  if (!order.photosViewed) {
                                    try {
                                      const is_viewed = await suppliesService.updateIsViewed(order.id, 'photos');
                                      setOrders(prev => prev.map(o => o.id === order.id ? { ...o, photosViewed: true, is_viewed } : o));
                                    } catch (err) {
                                      alert('Failed to mark Photos as viewed.');
                                      console.error(err);
                                    }
                                  }
                                }}
                              >
                                <i className="bi bi-eye"></i> {(order.photos && order.photos.length > 0) ? (order.photosViewed ? 'Viewed' : 'View') : 'View'} ({order.photos ? order.photos.length : 0})
                              </button>
                            </div>
                        </td>
                        <td>
                          <div className="follow-up-actions">
                            <button
                              className="btn btn-outline-success btn-sm"
                              onClick={() => setFollowUpModal(prev => ({
                                ...prev,
                                show: true,
                                orderId: order.id,
                                followUps: order.follow_ups || [],
                                isAdding: true
                              }))}
                            >
                              <i className="bi bi-plus"></i> Add
                            </button>
                            {order.follow_ups && order.follow_ups.length > 0 && (
                              <button
                                className={`btn btn-sm ${order.followUpsViewed ? 'btn-primary' : 'btn-danger'}`}
                                onClick={async () => {
                                  showFollowUps(order.id);
                                  if (!order.followUpsViewed) {
                                    try {
                                      const is_viewed = await suppliesService.updateIsViewed(order.id, 'follow_up');
                                      setOrders(prev => prev.map(o => o.id === order.id ? { ...o, followUpsViewed: true, is_viewed } : o));
                                    } catch (err) {
                                      alert('Failed to mark Follow Up as viewed.');
                                      console.error(err);
                                    }
                                  }
                                }}
                              >
                                <i className="bi bi-eye"></i> {order.followUpsViewed ? 'Viewed' : 'View'} ({order.follow_ups.length})
                              </button>
                            )}
                          </div>
                        </td>
                        <td className="text-truncate">{getLastFollowUp(order).name}</td>
                        <td className="text-truncate">{getLastFollowUp(order).date}</td>
                        <td className="text-truncate">
                            <span className={`badge stock-badge ${order.branch_stock_status === 'in stock' ? 'in-stock' : 'out-of-stock'}`}>
                              {order.branch_stock_status === 'in stock' ? 'IN STOCK' : 'OUT OF STOCK'}
                            </span>
                        </td>
                        <td className="text-truncate">
                            <span className={`badge stock-badge ${order.store_stock_status === 'in stock' ? 'in-stock' : 'out-of-stock'}`}>
                              {order.store_stock_status === 'in stock' ? 'IN STOCK' : 'OUT OF STOCK'}
                            </span>
                        </td>
                        {canAssignOrders() && (
                          <>
                            {/* ASSIGN TO Column */}
                            <td>
                              {editingAssignment[order.id] ? (
                                <div className="input-group input-group-sm supply-orders-minwidth-150">
                                  <select
                                    className="form-select form-select-sm"
                                    value={tempAssignments[order.id] || ''}
                                    onChange={(e) => setTempAssignments(prev => ({
                                      ...prev,
                                      [order.id]: e.target.value ? parseInt(e.target.value) : null
                                    }))}
                                  >
                                    <option value="">Unassigned</option>
                                    {purchaseUsers.map(user => (
                                      <option key={user.id} value={user.id}>
                                        {user.username}
                                      </option>
                                    ))}
                                  </select>
                                  <button
                                    className="btn btn-success btn-sm"
                                    onClick={() => saveAssignment(order.id)}
                                    title="Save assignment"
                                  >
                                    <i className="bi bi-check"></i>
                                  </button>
                                  <button
                                    className="btn btn-secondary btn-sm"
                                    onClick={() => cancelEditingAssignment(order.id)}
                                    title="Cancel"
                                  >
                                    <i className="bi bi-x"></i>
                                  </button>
                                </div>
                              ) : (
                                <button
                                  className="btn btn-outline-primary btn-sm"
                                  onClick={() => startEditingAssignment(order.id, order.assigned_to)}
                                  title="Click to assign"
                                >
                                  <i className="bi bi-person-plus"></i> Assign
                                </button>
                              )}
                            </td>
                            {/* ASSIGNED TO Column */}
                            <td>
                              {order.assigned_to_name ? (
                                <span className="badge bg-primary">
                                  {order.assigned_to_name}
                                </span>
                              ) : (
                                <span className="text-muted">Unassigned</span>
                              )}
                            </td>
                          </>
                        )}
                        <td>
                          <span className={`badge ${getStatusBadgeClass(order.status)}`}>
                            {order.status.toUpperCase()}
                          </span>
                        </td>
                        <td className="text-truncate">{order.updated_by_name ||'--'}</td>
                        <td>{formatDate(order.updated_at)}</td>
                        <td>
                          <div className="btn-group-sm d-flex gap-1" >
                            {editingStatus[order.id] ? (
                              <div className="input-group input-group-sm supply-orders-minwidth-150">
                                <select
                                  className="form-select form-select-sm"
                                  value={tempStatus[order.id] || order.status}
                                  onChange={(e) => setTempStatus(prev => ({
                                    ...prev,
                                    [order.id]: e.target.value
                                  }))}
                                >
                                  <option value="pending">Pending</option>
                                  <option value="done">Done</option>
                                  <option value="unavailable">Unavailable</option>
                                  <option value="limited stock">Limited Stock</option>
                                </select>
                                <button
                                  className="btn btn-success btn-sm"
                                  onClick={() => saveStatus(order.id)}
                                  title="Update status"
                                >
                                  <i className="bi bi-check"></i>
                                </button>
                                <button
                                  className="btn btn-secondary btn-sm"
                                  onClick={() => cancelEditingStatus(order.id)}
                                  title="Cancel"
                                >
                                  <i className="bi bi-x"></i>
                                </button>
                              </div>
                            ) : (
                              <>
                                <button
                                  className="btn btn-sm btn-primary"
                                  onClick={() => startEditingStatus(order.id, order.status)}
                                  title="Update Status"
                                >
                                  <i className="bi bi-arrow-repeat"></i>
                                </button>
                                <button
                                  className="btn btn-sm btn-danger ms-1"
                                  onClick={() => handleDeleteOrder(order.id)}
                                  title="Delete order"
                                >
                                  <i className="bi bi-trash"></i>
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                        <td>
                          {order.supply_order_number ? (
                            <span>{order.supply_order_number}</span>
                          ) : (
                            <span className="text-muted">--</span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
              </table>
            </div>
        </div>

          {/* All Modals */}
          
          {/* Notes Modal - Multiple Notes Support */}
          {notesModal.show && (
            <>
              {/* Overlay Backdrop - covers everything except modal */}
              <div
                className="modal-backdrop show"
                onClick={() => setNotesModal({ show: false, orderId: null, notes: [], newNote: '', isAdding: false })}
              ></div>
              {/* Modal above overlay, with all inputs inside */}
              <div className="modal show d-block supply-orders-modal-positioning" tabIndex={-1}>
                <div className="modal-dialog modal-lg">
                  <div className="modal-content">
                    <div className="modal-header">
                      <h5 className="modal-title">
                        <i className="bi bi-journal-text me-2"></i>
                        {notesModal.isAdding ? 'Add Note' : 'View Notes'}
                      </h5>
                      <button
                        type="button"
                        className="btn-close"
                        onClick={() => setNotesModal({ show: false, orderId: null, notes: [], newNote: '', isAdding: false })}
                      ></button>
                    </div>
                    <div className="modal-body">
                      <div className="card shadow-sm mx-auto supply-orders-maxwidth-600">
                        <div className="card-body">
                          {notesModal.isAdding ? (
                            <>
                              <h6 className="mb-3 supply-orders-fw-500">
                                <i className="bi bi-pencil-square me-2 text-orange"></i> Add Note
                              </h6>
                              <div className="mb-3">
                                <label className="form-label">Note</label>
                                <textarea
                                  className="form-control supply-orders-textarea-modal"
                                  rows={3}
                                  value={notesModal.newNote}
                                  onChange={e => setNotesModal(modal => ({ ...modal, newNote: e.target.value }))}
                                  placeholder="Enter note..."
                                />
                              </div>
                              <div className="d-flex gap-2">
                                <button
                                  className="btn btn-success supply-orders-modal-btn"
                                  onClick={async () => {
                                    if (!notesModal.orderId || !notesModal.newNote.trim()) return;
                                    // Add new note to the array with username
                                    const newNoteObj = {
                                      text: notesModal.newNote.trim(),
                                      timestamp: new Date().toLocaleString(),
                                      username: user?.username || 'Unknown'
                                    };
                                    const updatedNotesArr = [...notesModal.notes, newNoteObj];
                                    const updatedNotes = updatedNotesArr.map(n => JSON.stringify(n)).join('\n---\n');
                                    await suppliesService.updateNotes(notesModal.orderId, updatedNotes);
                                    setNotesModal({ show: false, orderId: null, notes: [], newNote: '', isAdding: false });
                                    await loadOrders();
                                    Swal.fire({
                                      icon: 'success',
                                      title: 'Note Added',
                                      text: 'Note has been added successfully.',
                                      timer: 1500,
                                      showConfirmButton: false
                                    });
                                  }}
                                >
                                  <i className="bi bi-check me-1"></i>
                                  Save
                                </button>
                                <button
                                  className="btn btn-secondary supply-orders-modal-btn"
                                  onClick={() => setNotesModal({ show: false, orderId: null, notes: [], newNote: '', isAdding: false })}
                                >
                                  Cancel
                                </button>
                              </div>
                            </>
                          ) : (
                            <>
                              <h6 className="mb-3 supply-orders-fw-500">
                                <i className="bi bi-journal-text me-2 text-orange"></i> Notes ({notesModal.notes.length})
                              </h6>
                              <div className="mb-3">
                                {notesModal.notes.length === 0 ? (
                                  <div className="text-muted">No notes available.</div>
                                ) : (
                                  <ul className="list-group">
                                    {notesModal.notes.map((noteObj, idx) => (
                                      <li key={idx} className="list-group-item">
                                        <div className="note-content">
                                          <pre>{noteObj.text}</pre>
                                          <span className="text-muted small">
                                            {noteObj.username ? `Added by: ${noteObj.username}` : ''}
                                            {noteObj.username ? ' | ' : ''}{noteObj.timestamp}
                                          </span>
                                        </div>
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </div>
                              <div className="d-flex gap-2">
                                <button
                                  className="btn btn-secondary supply-orders-modal-close-btn"
                                  onClick={() => setNotesModal({ show: false, orderId: null, notes: [], newNote: '', isAdding: false })}
                                >
                                  Close
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
      
          {/* Photo Modal */}
          {photoModal.show && (
            <>
              {/* Overlay Backdrop - covers everything except modal */}
              <div
                className="modal-backdrop show"
                onClick={() => setPhotoModal({ show: false, photos: [], currentIndex: 0 })}
              ></div>
              {/* Modal above overlay, with all inputs inside */}
              <div className="modal show d-block supply-orders-modal-positioning" tabIndex={-1}>
              <div className="modal-dialog modal-lg">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">
                      <i className="bi bi-images me-2"></i>
                      Order Photos ({photoModal.currentIndex + 1} of {photoModal.photos.length})
                    </h5>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setPhotoModal({ show: false, photos: [], currentIndex: 0 })}
                    ></button>
                  </div>
                  <div className="modal-body text-center">
                    {photoModal.photos.length > 0 && (
                      <>
                        <img
                          src={photoModal.photos[photoModal.currentIndex]}
                          alt={`Photo ${photoModal.currentIndex + 1}`}
                          className="img-fluid"
                          onClick={() => window.open(photoModal.photos[photoModal.currentIndex], '_blank')}
                        />
                        {photoModal.photos.length > 1 && (
                          <div className="mt-3">
                            <button
                              className="btn btn-secondary me-2"
                              onClick={() => setPhotoModal(prev => ({
                                ...prev,
                                currentIndex: prev.currentIndex > 0 ? prev.currentIndex - 1 : prev.photos.length - 1
                              }))}
                            >
                              <i className="bi bi-chevron-left"></i> Previous
                            </button>
                            <button
                              className="btn btn-secondary"
                              onClick={() => setPhotoModal(prev => ({
                                ...prev,
                                currentIndex: prev.currentIndex < prev.photos.length - 1 ? prev.currentIndex + 1 : 0
                              }))}
                            >
                              Next <i className="bi bi-chevron-right"></i>
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
         </>
          )}

          {/* Follow Up Modal */}
          {followUpModal.show && (
            <div className="modal show d-block" tabIndex={-1}>
              <div className="modal-dialog modal-lg">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">
                      <i className="bi bi-chat-text me-2"></i>
                      Follow-up Actions
                    </h5>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setFollowUpModal({
                        show: false,
                        orderId: null,
                        followUps: [],
                        isAdding: false,
                        newNotes: '',
                        newPhotos: [],
                        newStatus: ''
                      })}
                    ></button>
                  </div>
                  <div className="modal-body">
                    {/* Add New Follow Up */}
                    {followUpModal.isAdding && (
                      <div className="border rounded p-3 mb-3 bg-light">
                        <h6>Add New Follow-up</h6>
                        <div className="mb-3">
                          <label className="form-label">Notes</label>
                          <textarea
                            className="form-control"
                            rows={3}
                            value={followUpModal.newNotes}
                            onChange={(e) => setFollowUpModal(prev => ({
                              ...prev,
                              newNotes: e.target.value
                            }))}
                            placeholder="Enter follow-up notes..."
                          />
                        </div>
                        <div className="mb-3">
                          <label className="form-label">Photos</label>
                          <PhotoUploadComponent
                            onPhotosAdded={(photos) => setFollowUpModal(prev => ({
                              ...prev,
                              newPhotos: [...prev.newPhotos, ...photos]
                            }))}
                          />
                          {followUpModal.newPhotos.length > 0 && (
                            <div className="photo-preview-container mt-2">
                              {followUpModal.newPhotos.map((photo, index) => (
                                <div key={index} className="photo-preview">
                                  <img src={photo} alt={`Preview ${index + 1}`} />
                                  <button
                                    type="button"
                                    className="btn-remove-photo"
                                    onClick={() => setFollowUpModal(prev => ({
                                      ...prev,
                                      newPhotos: prev.newPhotos.filter((_, i) => i !== index)
                                    }))}
                                  >
                                    <i className="bi bi-x"></i>
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="mb-3">
                          <label className="form-label">Status</label>
                          <select
                            className="form-select"
                            value={followUpModal.newStatus}
                            onChange={(e) => setFollowUpModal(prev => ({ ...prev, newStatus: e.target.value }))}
                          >
                            <option value="">Select status</option>
                            <option value="ناقص">ناقص</option>
                            <option value="تبادل">تبادل</option>
                            <option value="اخراج جمركي">اخراج جمركي</option>
                            <option value="مستشفيات">مستشفيات</option>
                            <option value="صفحات">صفحات</option>
                          </select>
                        </div>
                        <div className="d-flex gap-2">
                          <button
                            className="btn btn-success"
                            onClick={addFollowUp}
                          >
                            <i className="bi bi-check me-1"></i>
                            Add Follow-up
                          </button>
                          <button
                            className="btn btn-secondary"
                            onClick={() => setFollowUpModal(prev => ({
                              ...prev,
                              isAdding: false,
                              newNotes: '',
                              newPhotos: [],
                              newStatus: ''
                            }))}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Existing Follow Ups rendered as a table */}
                    <div className="follow-up-timeline">
                      {followUpModal.followUps.length === 0 ? (
                        <div className="text-center py-3">
                          <i className="bi bi-chat-text display-4 text-muted"></i>
                          <p className="text-muted mt-2">No follow-ups yet</p>
                          {!followUpModal.isAdding && (
                            <button
                              className="btn btn-primary"
                              onClick={() => setFollowUpModal(prev => ({ ...prev, isAdding: true }))}
                            >
                              <i className="bi bi-plus me-1"></i>
                              Add First Follow-up
                            </button>
                          )}
                        </div>
                      ) : (
                        <div className="table-responsive">
                          <table className="table table-sm table-bordered mb-0">
                            <thead>
                              <tr>
                                <th>Name</th>
                                <th>Notes</th>
                                <th>Created At</th>
                                <th>Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {followUpModal.followUps.map((followUp) => (
                                <tr key={followUp.id}>
                                  <td className="align-middle">{followUp.user_name}</td>
                                  <td className="align-middle text-truncate" style={{ maxWidth: 420 }}>{followUp.notes}</td>
                                  <td className="align-middle text-truncate">{formatDate(followUp.created_at)}</td>
                                  <td className="align-middle">
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                      {followUp.photos && followUp.photos.length > 0 && (
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-outline-secondary"
                                          onClick={() => showPhotos(followUp.photos)}
                                        >
                                          View
                                        </button>
                                      )}
                                      <span style={{ marginLeft: 8 }}>{followUp.action || '--'}</span>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="modal-footer">
                    {!followUpModal.isAdding && followUpModal.followUps.length > 0 && (
                      <button
                        className="btn btn-primary"
                        onClick={() => setFollowUpModal(prev => ({ ...prev, isAdding: true }))}
                      >
                        <i className="bi bi-plus me-1"></i>
                        Add Follow-up
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setFollowUpModal({
                        show: false,
                        orderId: null,
                        followUps: [],
                        isAdding: false,
                        newNotes: '',
                        newPhotos: [],
                        newStatus: ''
                      })}
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Photo Upload Modal */}
          {photoUploadModal.show && (
            <>
              <div
                className="modal-backdrop show"
                onClick={() => setPhotoUploadModal({ show: false, orderId: null })}
              ></div>
              <div className="modal show d-block" tabIndex={-1}>
                <div className="modal-dialog">
                  <div className="modal-content">
                    <div className="modal-header">
                      <h5 className="modal-title">
                        <i className="bi bi-cloud-upload me-2"></i>
                        Add Photos
                      </h5>
                      <button
                        type="button"
                        className="btn-close"
                        onClick={() => setPhotoUploadModal({ show: false, orderId: null })}
                      ></button>
                    </div>
                    <div className="modal-body">
                      <PhotoUploadComponent
                        onPhotosAdded={async (photos: string[]) => {
                          try {
                            if (photoUploadModal.orderId && photos.length > 0) {
                              // Upload photos to the server
                              const files: File[] = [];
                              // Convert base64 images to files
                              for (let i = 0; i < photos.length; i++) {
                                const base64Data = photos[i];
                                const blob = await fetch(base64Data).then(res => res.blob());
                                const file = new File([blob], `photo${i}.jpg`, { type: 'image/jpeg' });
                                files.push(file);
                              }
                              // Call API to upload photos
                              await suppliesService.uploadPhotos(photoUploadModal.orderId, files);
                              // Refresh orders to show updated photos and ensure is_viewed status updates
                              setTimeout(loadOrders, 200);
                              setPhotoUploadModal({ show: false, orderId: null });
                              Swal.fire({
                                icon: 'success',
                                title: 'Photos Added',
                                text: 'Photos have been added successfully',
                                timer: 1500,
                                showConfirmButton: false
                              });
                            }
                          } catch (error) {
                            console.error('Error uploading photos:', error);
                            Swal.fire({
                              icon: 'error',
                              title: 'Upload Failed',
                              text: 'Failed to upload photos',
                              confirmButtonColor: '#ff6b35'
                            });
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Modal Backdrop */}
          {(photoModal.show || followUpModal.show || photoUploadModal.show) && (
            <div
              className="modal-backdrop show"
              onClick={() => {
                setPhotoModal({ show: false, photos: [], currentIndex: 0 });
                setFollowUpModal({
                  show: false,
                  orderId: null,
                  followUps: [],
                  isAdding: false,
                  newNotes: '',
                  newPhotos: [],
                  newStatus: ''
                });
                setPhotoUploadModal({ show: false, orderId: null });
              }}
            ></div>
          )}
        </div>
      </div>

      {/* Fixed Footer with Pagination */}
      {orders.length > 0 && (
        <div className="footer">
          <div className="d-flex flex-column flex-md-row align-items-center justify-content-between p-3 pagination-fixed">
            <div className="text-muted">
              <span>
                Showing {((pagination.currentPage - 1) * pagination.recordsPerPage) + 1} to {Math.min(pagination.currentPage * pagination.recordsPerPage, pagination.totalRecords)} of {pagination.totalRecords} entries
              </span>
            </div>
            <div className="d-flex flex-column flex-md-row align-items-center justify-content-center gap-3 w-100">
              <div className="text-muted large">
                <i className="bi bi-clock me-1"></i>
                Last updated: {lastRefresh.toLocaleTimeString()}
              </div>
              <div className="text-muted large">
                {pagination.totalRecords} records found
              </div>
            </div>
            <nav>
              <ul className="pagination pagination-sm mb-0">
                <li className={`page-item ${pagination.currentPage === 1 ? 'disabled' : ''}`}>
                  <button
                    className="page-link"
                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                    disabled={pagination.currentPage === 1}
                  >
                    Previous
                  </button>
                </li>
                {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                  const page = Math.max(1, pagination.currentPage - 2) + i;
                  if (page > pagination.totalPages) return null;
                  return (
                    <li key={page} className={`page-item ${pagination.currentPage === page ? 'active' : ''}`}>
                     <button
                        className="page-link"
                        onClick={() => handlePageChange(page)}
                      >
                        {page}
                      </button>
                    </li>
                  );
                })}
                <li className={`page-item ${pagination.currentPage === pagination.totalPages ? 'disabled' : ''}`}>
                  <button
                    className="page-link"
                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                    disabled={pagination.currentPage === pagination.totalPages}
                  >
                    Next
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      )}
    </>
   
  );
};

export default SupplyOrders;